import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Navigate, Link } from 'react-router-dom';
import { Form, Button, Message } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import get from 'lodash.get';
import querystring from 'querystring';

import Frame from './frame';
import VerificationForm from './verificationForm';
import RecoveryForm from './recoveryForm';

import { verifyMfa, clearMfa } from '../../modules/session';
import config from '../../constants';
import { verificationVariant } from '../../constants/multiFactorAuth';
import { hasTextEntryInResources } from '../template/templateHelper';
import { grecaptcha } from '../../helpers/grecaptcha';
import { getSafeReturnPath } from '../../helpers/returnUrl';
import './authentication.css';

const MFA_TIMEOUT_MS = 300000; // 5 minutes

class MfaVerification extends Component {
  constructor(props) {
    super(props);
    this.state = {
      variant: verificationVariant.authCode,
      authCode: null,
      recoveryCode: null,
      timeRemaining: this.getTimeRemaining()
    };
    this.timerInterval = null;
  }

  componentDidMount() {
    this.timerInterval = setInterval(() => {
      const remaining = this.getTimeRemaining();
      this.setState({ timeRemaining: remaining });
      if (remaining <= 0) {
        clearInterval(this.timerInterval);
        this.props.clearMfa();
      }
    }, 1000);
  }

  componentWillUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  getTimeRemaining() {
    if (!this.props.session.mfaTimestamp) return 0;
    return Math.max(0, this.props.session.mfaTimestamp + MFA_TIMEOUT_MS - Date.now());
  }

  formatTime(ms) {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  handleValueChange = (path, value) => {
    // Map VerificationForm/RecoveryForm callbacks to local state
    if (path.endsWith('.authCode')) {
      this.setState({ authCode: value });
    } else if (path.endsWith('.recoveryCode')) {
      this.setState({ recoveryCode: value });
    }
  };

  handleVerificationVariantChange = (value) => {
    this.setState({ variant: value, authCode: null, recoveryCode: null });
  };

  handleSubmit = () => {
    const { pendingSessionToken } = this.props.session;
    const { variant, authCode, recoveryCode } = this.state;

    const otp = variant === verificationVariant.authCode ? authCode : null;
    const otpRecovery = variant === verificationVariant.recoveryCode ? recoveryCode : null;

    const tokenCallback = (token) =>
      this.props.verifyMfa(pendingSessionToken, otp, otpRecovery, token);
    grecaptcha(tokenCallback, 'login');
  };

  handleBackToLogin = () => {
    this.props.clearMfa();
  };

  isError = () => {
    const { error, errorList } = this.props.session;
    return error && errorList && errorList.length > 0;
  };

  isExpiredTokenError = () => {
    const errorKey = get(this.props, 'session.errorList.0.error');
    return errorKey === 'pendingSessionToken.expired';
  };

  // Remap generic API error keys to MFA-specific translations
  static MFA_ERROR_OVERRIDES = {
    'model.invalidCredentials': 'model.invalidMfaCode'
  };

  getErrorTranslation = () => {
    const errorList = get(this.props, 'session.errorList');
    if (!errorList || errorList.length === 0) return [];

    return errorList.map((item, index) => {
      const currentErrorKey = get(
        this.props,
        `session.errorList.${index}.error`
      );
      const mappedKey = MfaVerification.MFA_ERROR_OVERRIDES[currentErrorKey] || currentErrorKey;
      const fullKey = `errors.api.${mappedKey}`;
      if (hasTextEntryInResources(fullKey)) {
        return I18n.t(fullKey);
      }
      return I18n.t('errors.api.general', {
        errorKey: currentErrorKey,
        fullKey: I18n.t(fullKey)
      });
    });
  };

  render() {
    const { session } = this.props;

    // If authenticated (MFA verified), redirect to home or return URL.
    // This must be checked before the pendingSessionToken guard below:
    // VERIFY_MFA_SUCCESS sets `key` and clears `pendingSessionToken` in the
    // same update, so checking the token first would bounce a freshly
    // verified user to /login and drop the return URL.
    if (session.key !== null && session.key !== undefined) {
      if (window.location.search && window.location.search.length > 1) {
        const q = querystring.decode(window.location.search.slice(1));
        const returnPath = getSafeReturnPath(q.return);
        if (returnPath) {
          return <Navigate to={returnPath} replace />;
        }
      }
      return <Navigate to="/" replace />;
    }

    // No pending token (and not authenticated) — redirect to login
    if (!session.pendingSessionToken) {
      return <Navigate to="/login" replace />;
    }

    // If expired token error, redirect to login
    if (this.isError() && this.isExpiredTokenError()) {
      return <Navigate to="/login" replace />;
    }

    const { timeRemaining } = this.state;

    return (
      <Frame>
        <Form onSubmit={this.handleSubmit} error={this.isError()}>
          <div className="verification-form">
            <h1 className="variant-title">
              {I18n.t('authentication.mfa.heading')}
            </h1>
            <div className="variant-description">
              {I18n.t('authentication.mfa.description')}
            </div>

            {timeRemaining > 0 && (
              <div className="mfa-countdown" style={{ textAlign: 'center', marginBottom: '1em', color: timeRemaining < 60000 ? '#db2828' : undefined }}>
                {I18n.t('authentication.mfa.expiresIn', {
                  time: this.formatTime(timeRemaining)
                })}
              </div>
            )}

            {this.state.variant === verificationVariant.authCode && (
              <VerificationForm
                path="mfa"
                handleValueChange={this.handleValueChange}
                handleVerificationVariantChange={this.handleVerificationVariantChange}
              />
            )}
            {this.state.variant === verificationVariant.recoveryCode && (
              <RecoveryForm
                path="mfa"
                value={this.state.recoveryCode}
                handleValueChange={this.handleValueChange}
                handleVerificationVariantChange={this.handleVerificationVariantChange}
              />
            )}

            {this.isError() && (
              <Message style={{ margin: 'auto' }} error list={this.getErrorTranslation()} />
            )}

            <div className="verification-action-button">
              <Button
                primary
                loading={session.processing}
                disabled={session.processing}
              >
                {I18n.t('authentication.mfa.verify')}
              </Button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <Link to="/login" onClick={this.handleBackToLogin}>
                {I18n.t('authentication.mfa.backToLogin')}
              </Link>
            </div>
            <div className="verification-contact-us" style={{ marginTop: '1rem' }}>
              {I18n.t('authentication.mfa.needHelp')}{' '}
              <a href={config.contactUsUrl}>
                {I18n.t('authentication.mfa.supportTeam')}
              </a>
            </div>
          </div>
        </Form>
      </Frame>
    );
  }
}

const mapState = ({ session }) => ({
  session: session
});

const mapDispatch = (dispatch) => ({
  verifyMfa(pendingSessionToken, otp, otpRecoveryCode, verificationToken) {
    dispatch(verifyMfa(pendingSessionToken, otp, otpRecoveryCode, verificationToken));
  },
  clearMfa() {
    dispatch(clearMfa());
  }
});

export default connect(mapState, mapDispatch)(MfaVerification);
